import { getDocs, query, Timestamp, where, writeBatch } from 'firebase/firestore';
import { db } from '../config/firebase';
import { countedGetDocs } from '../data/firestoreReads';
import {
  binderPlacementCountDoc,
  binderPlacementCountsCollection,
  binderPlacementDoc,
  binderPlacementsCollection,
  userBinderDoc,
  userBindersCollection,
} from '../firebase/collections';
import { deleteRefsInBatches } from './firestoreBatch';
import type { BinderPlacement } from '../types';

function placementFromDoc(slotKey: string, data: Record<string, unknown>): BinderPlacement {
  const updatedAt = (data.updatedAt as Timestamp | undefined)?.toMillis?.() || 0;
  return {
    slotKey,
    cardId: (data.cardId as string) || '',
    pageIndex: (data.pageIndex as number) ?? 0,
    slotIndex: (data.slotIndex as number) ?? 0,
    foil: Boolean(data.foil),
    createdAt: (data.createdAt as Timestamp | undefined)?.toMillis?.() || updatedAt,
    updatedAt,
  };
}

/**
 * Which placements must go when owned copies drop to `maxTotal`/`maxFoil`.
 *
 * Oldest placements are kept: a slot the user arranged long ago survives a
 * later quantity correction, and the most recently added one is dropped first.
 * Exported for tests — this decides which of a user's arrangements are deleted.
 */
export function slotKeysToRemove(
  placements: BinderPlacement[],
  maxTotal: number,
  maxFoil: number,
): string[] {
  const sorted = [...placements].sort((a, b) => a.updatedAt - b.updatedAt);
  const kept: BinderPlacement[] = [];
  let total = 0;
  let foil = 0;

  for (const placement of sorted) {
    const nextTotal = total + 1;
    const nextFoil = foil + (placement.foil ? 1 : 0);
    if (nextTotal <= maxTotal && nextFoil <= maxFoil) {
      kept.push(placement);
      total = nextTotal;
      foil = nextFoil;
    }
  }

  const keptKeys = new Set(kept.map((placement) => placement.slotKey));
  return placements.filter((placement) => !keptKeys.has(placement.slotKey)).map((p) => p.slotKey);
}

function countRemainingPlacements(placements: BinderPlacement[]): { total: number; foil: number } {
  return placements.reduce(
    (count, placement) => ({
      total: count.total + 1,
      foil: count.foil + (placement.foil ? 1 : 0),
    }),
    { total: 0, foil: 0 },
  );
}

/** One card's placements that no longer fit its owned copy counts. */
export interface OrphanedPlacementGroup {
  cardId: string;
  /** Slot keys to delete. */
  slotKeys: string[];
  /** Placement counts left for this card once the deletions land. */
  remaining: { total: number; foil: number };
}

/**
 * Which of a binder's placements exceed what the user still owns.
 *
 * Pure, and takes placements the caller already holds — the binder page has
 * both the live placements and the live collection, so reconciling there costs
 * no reads at all, unlike doing it on every quantity decrease.
 */
export function planPlacementReconciliation(
  placements: BinderPlacement[],
  owned: Record<string, { quantity: number; quantityFoil?: number }>,
): OrphanedPlacementGroup[] {
  const byCard = new Map<string, BinderPlacement[]>();
  for (const placement of placements) {
    if (!placement.cardId) continue;
    const group = byCard.get(placement.cardId);
    if (group) group.push(placement);
    else byCard.set(placement.cardId, [placement]);
  }

  const groups: OrphanedPlacementGroup[] = [];
  for (const [cardId, cardPlacements] of byCard) {
    const entry = owned[cardId];
    const maxTotal = Math.max(0, entry?.quantity ?? 0);
    const maxFoil = Math.min(Math.max(0, entry?.quantityFoil ?? 0), maxTotal);
    const slotKeys = slotKeysToRemove(cardPlacements, maxTotal, maxFoil);
    if (slotKeys.length === 0) continue;

    const removed = new Set(slotKeys);
    groups.push({
      cardId,
      slotKeys,
      remaining: countRemainingPlacements(
        cardPlacements.filter((placement) => !removed.has(placement.slotKey)),
      ),
    });
  }
  return groups;
}

/**
 * Delete placements in one binder that the user no longer owns copies for.
 *
 * Performs no reads: `placements` and `owned` come from listeners the binder
 * page already holds. Returns the number of placements removed.
 */
export async function reconcileBinderPlacements(
  uid: string,
  binderId: string,
  placements: BinderPlacement[],
  owned: Record<string, { quantity: number; quantityFoil?: number }>,
): Promise<number> {
  const groups = planPlacementReconciliation(placements, owned);
  if (groups.length === 0) return 0;

  const now = Timestamp.now();
  const batch = writeBatch(db);
  let removed = 0;

  for (const group of groups) {
    for (const slotKey of group.slotKeys) {
      batch.delete(binderPlacementDoc(uid, binderId, slotKey));
      removed += 1;
    }
    const countRef = binderPlacementCountDoc(uid, binderId, group.cardId);
    if (group.remaining.total > 0) {
      batch.set(countRef, {
        cardId: group.cardId,
        total: group.remaining.total,
        foil: Math.min(group.remaining.foil, group.remaining.total),
        updatedAt: now,
      });
    } else {
      batch.delete(countRef);
    }
  }

  batch.update(userBinderDoc(uid, binderId), { updatedAt: now });
  await batch.commit();
  return removed;
}

/**
 * Remove every custom-binder placement + count doc after the collection is cleared.
 * Set binders mirror live collection data, so they need no cleanup.
 */
export async function clearAllCustomBinderPlacements(uid: string): Promise<void> {
  const bindersSnap = await getDocs(userBindersCollection(uid));
  const now = Timestamp.now();

  for (const binderDoc of bindersSnap.docs) {
    if ((binderDoc.data().kind as string | undefined) === 'set') continue;

    const [placementsSnap, countsSnap] = await Promise.all([
      getDocs(binderPlacementsCollection(uid, binderDoc.id)),
      getDocs(binderPlacementCountsCollection(uid, binderDoc.id)),
    ]);
    const refs = [
      ...placementsSnap.docs.map((docSnap) => docSnap.ref),
      ...countsSnap.docs.map((docSnap) => docSnap.ref),
    ];
    if (refs.length === 0) continue;

    await deleteRefsInBatches(db, refs);
    const binderBatch = writeBatch(db);
    binderBatch.update(userBinderDoc(uid, binderDoc.id), { updatedAt: now });
    await binderBatch.commit();
  }
}
